"use client";
import { useErrorHandler } from "@/components/hooks/use-error-handler";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { DISABLE_BUTTON_DURATION_MS } from "@/constants";
import {
  sendPasswordResetEmail,
  type SendPasswordResetEmailError,
  type SendPasswordResetEmailFormData,
} from "@/server/actions/members/forgot-password/send-password-reset-email";
import { pagesPath } from "@/utils/pages-path";
import { zodResolver } from "@hookform/resolvers/zod";
import TextLogo from "@public/text-logo.svg";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { MdChevronLeft } from "react-icons/md";
import { z } from "zod";

const sendPasswordResetEmailFailureMessage = (
  error: SendPasswordResetEmailError,
) => {
  switch (error) {
    case "validationError":
      return "パスワード再設定のメール送信に失敗しました。入力内容に誤りがないかご確認ください。";
    case "exceedRateLimitError":
      return "パスワード再設定は一分間に一度しか行えません、時間をおいてもう一度お試しください。";
    case "emailNotFoundError":
      return "パスワード再設定のメール送信に失敗しました。入力されたメールアドレスは登録されていません。";
    case "alreadyLoggedIn":
      return "ログイン中のため、処理を中断しました。";
    case "accountDeletedError":
      return "このアカウントは削除されています";
    default:
      return "パスワード再設定のメール送信に失敗しました。時間をおいてもう一度お試しいただくか、サポートにお問い合わせください。";
  }
};

export const ForgotPasswordPage = () => {
  const handleError = useErrorHandler();
  const router = useRouter();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const formSchema = z.object({
    email: z.string().min(1, {
      message: "メールアドレスが入力されていません。",
    }),
  });
  const form = useForm<SendPasswordResetEmailFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    setSubmitting(true);
    const result = await sendPasswordResetEmail(data);

    if (result.success) {
      toast({
        variant: "success",
        title:
          "パスワード再設定のメールを入力されたメールアドレスに送信しました。",
      });
      router.push(pagesPath.members.login_with_otp.$url().path);
    } else {
      setTimeout(() => setSubmitting(false), DISABLE_BUTTON_DURATION_MS); // 連打対策
      toast({
        variant: "error",
        title: sendPasswordResetEmailFailureMessage(result.error),
      });
      if (result.error === "alreadyLoggedIn") {
        handleError("ALREADY_LOGGED_IN");
      }
    }
  });

  return (
    <div className="m-6 flex h-screen flex-col items-center md:mt-24">
      <Form {...form}>
        <form onSubmit={onSubmit} className="w-full max-w-lg">
          <h1>
            <TextLogo aria-label="VCスタートアップ健康保険組合" />
          </h1>
          <Separator className="my-6 bg-monotone-2" />

          <div className="flex flex-col gap-10">
            <div>
              <Button variant="ghost" className="px-0" asChild>
                <Link href={pagesPath.members.login.$url().path}>
                  <MdChevronLeft size={24} />
                  戻る
                </Link>
              </Button>
            </div>

            <FormField
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel className="font-bold">
                    ログイン用のメールアドレス：
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="メールアドレス"
                      autoComplete="email"
                      type="email"
                      isInvalid={fieldState.invalid}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={form.formState.isSubmitting || submitting}
            >
              パスワード再発行のメールを送信
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
